<script>
import tenantService from "@/services/tenant";
import { FEATURE_GROUPS } from "@/services/feature-catalog";

/**
 * @component TenantDetail
 * @description Detalle de un tenant para el platform-admin. Tabs: Identidad,
 *              Módulos, Branding, Integraciones. Cada tab tiene su propio
 *              save independiente (no hay un save global).
 *
 *              SEMÁNTICA DEL DEFAULT en features: si una clave no está en el
 *              objeto `features`, el switch se muestra como ON (que es lo
 *              que estaría haciendo el backend de runtime). Al guardar, sí
 *              se persiste el valor explícito de cada switch para que quede
 *              determinístico.
 */
export default {
  name: "TenantDetail",

  data() {
    return {
      isLoading: false,
      isSaving: false,
      activeTab: "identity",
      tenant: null,
      // Buffers editables — se inicializan cuando llega el tenant
      identityForm: {},
      featuresForm: {},
      brandingForm: {},
      // UC-REG-068 — sender de emails por org
      notificationsForm: {
        emailFrom: "",
        emailFromName: "",
        patientAssignmentEnabled: true,
      },
      // UC-REG-067 — toggle policonsultorio compartido (FILTER_ALL_PATIENT)
      sharePatientsAcrossDoctors: false,
      featureGroups: FEATURE_GROUPS,
    };
  },

  created() {
    this.load();
  },

  watch: {
    "$route.params.id"() {
      this.load();
    },
  },

  methods: {
    /**
     * @description Trae el tenant por ID y carga los buffers de cada tab.
     * @returns {Promise<void>}
     */
    async load() {
      this.isLoading = true;
      try {
        const tenant = await tenantService.getById(this.$route.params.id);
        this.tenant = tenant;
        this.identityForm = {
          name: tenant.name || "",
          key: tenant.key || "",
          kind: tenant.kind || "consultorio",
          domain: tenant.domain || "",
          timezone: tenant.timezone || "America/Argentina/Buenos_Aires",
          internalNotes: tenant.internalNotes || "",
          status: tenant.status || "active",
          preset: tenant.preset || "",
          email: tenant.email || "",
        };
        // Mapa { key: bool }. Si la clave no está, no la metemos al buffer —
        // el switch lee del computed `isFeatureOn(key)` que devuelve default-ON.
        this.featuresForm = { ...(tenant.features || {}) };
        this.brandingForm = { ...(tenant.branding || {}) };
        // UC-REG-068 — hidratar notifications (sender custom de la org)
        this.notificationsForm = {
          emailFrom:
            (tenant.notifications && tenant.notifications.emailFrom) || "",
          emailFromName:
            (tenant.notifications && tenant.notifications.emailFromName) || "",
          patientAssignmentEnabled:
            tenant.notifications &&
            tenant.notifications.patientAssignmentEnabled !== false,
        };
        // UC-REG-067 — leer toggle FILTER_ALL_PATIENT
        try {
          const sharing = await tenantService.getSharing(tenant._id);
          this.sharePatientsAcrossDoctors =
            !!sharing.sharePatientsAcrossDoctors;
        } catch (e) {
          this.sharePatientsAcrossDoctors = false;
        }
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "No pudimos traer el tenant."
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Devuelve si una feature está encendida según el buffer.
     *              Default-ON cuando la clave no está seteada.
     * @param {string} key
     * @returns {boolean}
     */
    isFeatureOn(key) {
      const v = this.featuresForm[key];
      if (v === undefined || v === null) return true;
      return Boolean(v);
    },

    /**
     * @description Toggle de una feature en el buffer (no persiste todavía).
     * @param {string} key
     */
    toggleFeature(key) {
      this.$set(this.featuresForm, key, !this.isFeatureOn(key));
    },

    /**
     * @description Prende/apaga todas las features de un grupo (botones "Todo
     *              ON" / "Todo OFF" al lado del título del grupo).
     * @param {Object} group
     * @param {boolean} value
     */
    setGroup(group, value) {
      group.features.forEach((f) => {
        this.$set(this.featuresForm, f.key, value);
      });
    },

    /**
     * @description Guarda identidad — PUT /tenants/:id
     */
    async saveIdentity() {
      this.isSaving = true;
      try {
        const updated = await tenantService.updateIdentity(
          this.tenant._id,
          this.identityForm
        );
        this.tenant = updated;
        this.$message.success("Identidad guardada.");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error guardando identidad."
        );
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Guarda features — PUT /tenants/:id/features
     *              Mandamos el mapa entero (no es delta).
     */
    async saveFeatures() {
      this.isSaving = true;
      try {
        const updated = await tenantService.updateFeatures(
          this.tenant._id,
          this.featuresForm
        );
        this.tenant = updated;
        this.featuresForm = { ...(updated.features || {}) };
        this.$message.success(
          "Features guardadas. Algunos cambios pueden requerir re-login."
        );
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error guardando features."
        );
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Resetea el buffer de features al estado del backend.
     */
    resetFeatures() {
      this.featuresForm = { ...(this.tenant.features || {}) };
    },

    /**
     * @description Guarda branding — PUT /tenants/:id/branding
     */
    async saveBranding() {
      this.isSaving = true;
      try {
        const updated = await tenantService.updateBranding(
          this.tenant._id,
          this.brandingForm
        );
        this.tenant = updated;
        this.$message.success("Branding guardado.");
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error guardando branding."
        );
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description UC-REG-068. Guarda notifications (email From custom) —
     *              PUT /tenants/:id/notifications.
     */
    async saveNotifications() {
      this.isSaving = true;
      try {
        const res = await tenantService.updateNotifications(
          this.tenant._id,
          this.notificationsForm
        );
        this.tenant.notifications = res.notifications;
        this.$message.success(
          "Notificaciones guardadas. Recordá verificar el dominio en SES si configuraste un emailFrom custom."
        );
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error guardando notificaciones."
        );
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description UC-REG-067. Toggle "compartir pacientes entre médicos" —
     *              PUT /tenants/:id/sharing. Reemplaza el insert manual de
     *              Parametrization{ FILTER_ALL_PATIENT } via mongoshell.
     */
    async saveSharing() {
      this.isSaving = true;
      try {
        await tenantService.updateSharing(
          this.tenant._id,
          this.sharePatientsAcrossDoctors
        );
        this.$message.success(
          this.sharePatientsAcrossDoctors
            ? "Modo compartido activado: todos los médicos verán todos los pacientes de la org."
            : "Modo estricto activado: cada médico verá solo sus pacientes."
        );
      } catch (err) {
        this.$message.error(
          err?.response?.data?.message || "Error guardando configuración."
        );
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * @description Volver a la lista de tenants.
     */
    goBack() {
      this.$router.push({ name: "platform-tenants" });
    },
  },
};
</script>

<template lang="pug">
section.tenant-detail-page
  spinner(v-if="isLoading")
  template(v-else-if="tenant")
    .detail-header
      a.back-link(@click="goBack") ← Volver a Tenants
      h1.detail-title
        span.avatar {{ (tenant.name || tenant.key || '??').slice(0,2).toUpperCase() }}
        | {{ tenant.name || tenant.key }}

    .tabs
      .tab(:class="{ active: activeTab === 'identity' }" @click="activeTab = 'identity'") Identidad
      .tab(:class="{ active: activeTab === 'features' }" @click="activeTab = 'features'") Módulos
      .tab(:class="{ active: activeTab === 'branding' }" @click="activeTab = 'branding'") Branding
      .tab(:class="{ active: activeTab === 'operation' }" @click="activeTab = 'operation'") Operación
      .tab(:class="{ active: activeTab === 'integrations' }" @click="activeTab = 'integrations'") Integraciones

    //- ─────────── TAB IDENTIDAD ───────────
    .tab-body(v-if="activeTab === 'identity'")
      .form-grid
        .form-field
          label Nombre comercial
          input(v-model="identityForm.name" type="text")
        .form-field
          label Slug
          input(v-model="identityForm.key" type="text" disabled)
          small Identificador interno, no se cambia post-creación.
        .form-field
          label Dominio
          input(v-model="identityForm.domain" type="text" placeholder="ej. tenant.ecosistemaquantux.com")
        .form-field
          label Email de la organización
          input(v-model="identityForm.email" type="email")
        .form-field
          label Tipo de tenant
          select(v-model="identityForm.kind")
            option(value="consultorio") Consultorio
            option(value="ooss") Obra Social
            option(value="both") Ambos
        .form-field
          label Timezone
          select(v-model="identityForm.timezone")
            option(value="America/Argentina/Buenos_Aires") America/Argentina/Buenos_Aires
            option(value="America/Argentina/Cordoba") America/Argentina/Cordoba
            option(value="America/Argentina/Mendoza") America/Argentina/Mendoza
        .form-field
          label Estado
          select(v-model="identityForm.status")
            option(value="active") Activo
            option(value="suspended") Suspendido
        .form-field
          label Preset comercial
          input(v-model="identityForm.preset" type="text" placeholder="consultorio-ws")
        .form-field.full
          label Notas internas (solo platform-admin)
          textarea(v-model="identityForm.internalNotes" rows="3")
      .form-footer
        button.btn-secondary(@click="load") Cancelar
        button.btn-primary(@click="saveIdentity" :disabled="isSaving") Guardar cambios

    //- ─────────── TAB MÓDULOS ───────────
    .tab-body(v-if="activeTab === 'features'")
      .preset-banner(v-if="tenant.preset")
        | Este tenant usa el preset
        strong  "{{ tenant.preset }}"
        | . Cambios manuales abajo se guardan como override.
      .feature-group(v-for="group in featureGroups" :key="group.id")
        .feature-group-header
          span.group-title {{ group.title }}
          span.group-actions
            a(@click="setGroup(group, false)") Apagar todo
            a(@click="setGroup(group, true)") Prender todo
        .feature-row(v-for="feature in group.features" :key="feature.key")
          label.switch
            input(type="checkbox" :checked="isFeatureOn(feature.key)" @change="toggleFeature(feature.key)")
            span.slider
          .feature-meta
            .feature-label
              | {{ feature.label }}
              span.feature-key {{ feature.key }}
            .feature-desc {{ feature.description }}
      .form-footer
        button.btn-secondary(@click="resetFeatures") Cancelar cambios
        button.btn-primary(@click="saveFeatures" :disabled="isSaving") Guardar configuración

    //- ─────────── TAB BRANDING ───────────
    .tab-body(v-if="activeTab === 'branding'")
      .form-grid
        .form-field
          label Logo principal (URL)
          input(v-model="brandingForm.logoUrl" type="text" placeholder="https://...")
          small Subí el SVG/PNG a S3 y pegá la URL acá.
        .form-field
          label Favicon (URL)
          input(v-model="brandingForm.faviconUrl" type="text" placeholder="https://...")
        .form-field
          label Color primario
          input(v-model="brandingForm.colorPrimary" type="text" placeholder="#002D5E")
        .form-field
          label Color acento
          input(v-model="brandingForm.colorAccent" type="text" placeholder="#0A3D7A")
        .form-field
          label Tipografía
          input(v-model="brandingForm.fontFamily" type="text" placeholder="Plus Jakarta Sans")
        .form-field
          label Bundle ID app mobile
          input(v-model="brandingForm.mobileBundleId" type="text" placeholder="com.tenant.app")
        .form-field.full
          label Copy del footer (emails)
          textarea(v-model="brandingForm.footerCopy" rows="2")
      .form-footer
        button.btn-secondary(@click="load") Cancelar
        button.btn-primary(@click="saveBranding" :disabled="isSaving") Guardar branding

    //- ─────────── TAB OPERACIÓN (UC-REG-067 + UC-REG-068) ───────────
    .tab-body(v-if="activeTab === 'operation'")
      .form-grid
        //- UC-REG-067: toggle FILTER_ALL_PATIENT
        .form-field.full
          .toggle-row
            label.switch
              input(
                type="checkbox"
                :checked="sharePatientsAcrossDoctors"
                @change="sharePatientsAcrossDoctors = !sharePatientsAcrossDoctors"
              )
              span.slider
            .toggle-meta
              .toggle-label Compartir pacientes entre médicos
              .toggle-desc
                | OFF (default): cada médico ve solo sus pacientes
                | (aquellos donde fue el creador o tuvo turno/episode).
                br
                | ON: todos los médicos de la org ven todos los pacientes
                | activos. Ideal para policonsultorios donde se cubren guardias
                | y comparten equipo administrativo.
        .form-footer
          button.btn-primary(@click="saveSharing" :disabled="isSaving") Guardar modo

      hr.section-divider

      //- UC-REG-068: email From custom de la org
      h3.subsection-title Email remitente (custom de la org)
      p.subsection-desc
        | Configurá un email From propio para esta org. Si lo dejás vacío,
        | los emails se envían desde el sender default del tenant.
        br
        strong Requiere: el dominio del email debe estar verificado en AWS SES de la cuenta del tenant.
        | Si no, los envíos van a fallar y caer al sender default (con warning en log).

      .form-grid
        .form-field
          label Email remitente
          input(
            v-model="notificationsForm.emailFrom"
            type="email"
            placeholder="noreply@tucentro.com"
          )
          small Si lo dejás vacío, usa el del tenant.
        .form-field
          label Nombre del remitente (display)
          input(
            v-model="notificationsForm.emailFromName"
            type="text"
            placeholder="Centro Médico Norte"
          )
          small Opcional. Si no, usa el nombre del tenant.
        .form-field.full
          label.checkbox-inline
            input(
              type="checkbox"
              v-model="notificationsForm.patientAssignmentEnabled"
            )
            span Habilitar notificaciones de asignación de pacientes
      .form-footer
        button.btn-primary(@click="saveNotifications" :disabled="isSaving") Guardar emails

    //- ─────────── TAB INTEGRACIONES (placeholder) ───────────
    .tab-body(v-if="activeTab === 'integrations'")
      .integ-placeholder
        h3 Próximamente
        p Las integraciones (webhook entrante de turnos, prescriptor externo, PACS, mailer dedicado) se configuran acá. En esta fase del tablero la sección es referencial — el manejo concreto del webhook de turnos para Vilella lo vamos a sumar en la próxima iteración.
</template>

<style lang="scss" scoped>
.tenant-detail-page {
  padding: 24px 32px;
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont,
    sans-serif;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}
.detail-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 4px;
}
.back-link {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.detail-title {
  margin: 8px 0 0;
  font-size: 22px;
  font-weight: 800;
  color: #002d5e;
  display: flex;
  align-items: center;
  gap: 12px;
}
.detail-title .avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #002d5e;
  color: white;
  font-size: 13px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin-top: 18px;
}
.tab {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}
.tab.active {
  color: #002d5e;
  border-bottom-color: #002d5e;
}
.tab-body {
  padding: 24px 0;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-field.full {
  grid-column: 1 / -1;
}
.form-field label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}
.form-field input,
.form-field select,
.form-field textarea {
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  font-size: 14px;
  color: #002d5e;
  font-family: inherit;
  background: #fff;
}
.form-field input:disabled {
  background: #f8fafc;
  color: #94a3b8;
}
.form-field small {
  font-size: 12px;
  color: #94a3b8;
}
.form-footer {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.btn-primary {
  padding: 10px 20px;
  border-radius: 10px;
  background: #002d5e;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
}
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-secondary {
  padding: 10px 20px;
  border-radius: 10px;
  background: #fff;
  color: #0a3d7a;
  font-weight: 700;
  font-size: 14px;
  border: 1.5px solid #e2e8f0;
  cursor: pointer;
}
.preset-banner {
  padding: 14px 18px;
  background: #eef3fa;
  border-radius: 10px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #475569;
}
.preset-banner strong {
  color: #002d5e;
}
.feature-group {
  margin-bottom: 24px;
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
}
.feature-group:first-of-type {
  border-top: none;
  padding-top: 0;
}
.feature-group-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  align-items: baseline;
}
.group-title {
  font-size: 13px;
  font-weight: 800;
  color: #002d5e;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.group-actions a {
  color: #0a3d7a;
  font-weight: 700;
  font-size: 12px;
  text-decoration: none;
  margin-left: 14px;
  cursor: pointer;
}
.feature-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
}
.feature-meta {
  flex: 1;
}
.feature-label {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
}
.feature-key {
  font-size: 11px;
  color: #94a3b8;
  font-family: "SF Mono", Menlo, Monaco, monospace;
  margin-left: 6px;
}
.feature-desc {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}
.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.switch .slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 999px;
  cursor: pointer;
  transition: 0.18s;
}
.switch .slider::before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.18s;
}
.switch input:checked + .slider {
  background: #002d5e;
}
.switch input:checked + .slider::before {
  transform: translateX(18px);
}
.integ-placeholder {
  padding: 32px;
  text-align: center;
  background: #f8fafc;
  border-radius: 12px;
}
.integ-placeholder h3 {
  color: #002d5e;
  margin: 0 0 8px;
  font-size: 16px;
}
.integ-placeholder p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
  max-width: 540px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}
</style>
